<template>
    <TprmModal
        additional-class-name="EditEmailTemplateModal"
        size="lg"
        :allow-close="!submitting"
        :open="open"
        title="Edit email template"
        @close="$emit('close')"
    >
        <form @submit.prevent="onSubmit">
            <JsonSchemaCustomForm v-slot="{ fields }" v-model="value" :disabled="submitting" :form="form">
                <JsonSchemaCustomFormElement :field="fields.summaryTemplate" />
                <JsonSchemaCustomFormElement :field="fields.descriptionTemplate" />
            </JsonSchemaCustomForm>
        </form>

        <template #footer>
            <NewButton
                color="primary"
                :loading="submitting"
                :disabled="!valid || submitting"
                @click="onSubmit"
            >
                Save
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import createForm from '@evidentid/json-schema/createForm';
    import { NotificationConfiguration } from '@evidentid/tprm-portal-lib/models/notification-configuration';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    import { buildEmailTemplateSchema } from '../schemas/buildEmailTemplateSchema';

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires. This is the family observed live during MODE-3 QA at
    // #/aleksandrpf/notifications/email-settings.
    @Component({
        components: {
            TprmModal,
            NewButton,
            JsonSchemaCustomForm,
            JsonSchemaCustomFormElement,
        },
        emits: [ 'close', 'submit' ],
    })
    export default class EditEmailTemplateModal extends Vue {
        @Prop({ type: Boolean, default: false })
        private open!: boolean;

        @Prop({ type: Boolean, default: false })
        private submitting!: boolean;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: string[];

        @Prop({ type: Object, default: () => ({}) })
        private initialValue!: NotificationConfiguration;

        private form = createForm(buildEmailTemplateSchema(this.customProperties));
        private value = this.form.getValue(this.initialValue, true);

        @Watch('customProperties')
        private onCustomPropertiesChange(): void {
            this.form = createForm(buildEmailTemplateSchema(this.customProperties));
        }

        private get valid(): boolean {
            return this.form.isValid(this.value, true);
        }

        private onSubmit(event: Event): void {
            event.preventDefault();
            if (this.valid && !this.submitting) {
                // The PUT replaces the notification type's entire configuration, so fields the
                // form does not edit (e.g. initEnabled, remindersEnabled) must be carried through
                // explicitly instead of being dropped by whatever the form happens to emit.
                this.$emit('submit', { ...this.initialValue, ...this.value });
            }
        }
    }
</script>
